import { useEffect, useState } from "react";
import { Store, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, errorMessage } from "@/lib/api";

export function BusinessAccounts({ onChange }) {
  const [rows, setRows] = useState(null);
  const [target, setTarget] = useState(null);
  const load = () => api.get("/admin/businesses").then((r) => setRows(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const remove = async () => {
    try {
      const { data } = await api.delete(`/admin/businesses/${target.id}`);
      toast.success(`Removed ${target.name || target.email} and ${data.removed_listings} listing${data.removed_listings === 1 ? "" : "s"}`);
      setTarget(null); load(); onChange?.();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  if (!rows) return null;
  return (
    <section data-testid="owner-business-accounts" className="mt-10 rounded-2xl bg-[#151C2C] border border-[#26334D] p-5">
      <h3 className="font-bold mb-3 flex items-center gap-2"><Store className="h-4 w-4 text-[#FFB800]" /> Business accounts <span className="text-xs text-slate-500 font-normal">({rows.length})</span></h3>
      {rows.length === 0 && <p className="text-sm text-slate-500">No business accounts yet.</p>}
      {rows.map((b) => (
        <div key={b.id} data-testid={`owner-business-row-${b.id}`} className="flex items-center gap-3 text-sm py-3 border-t border-[#26334D] first:border-0">
          <div className="flex-1 min-w-0">
            <p className="font-semibold truncate">{b.name} <span className="text-slate-500 font-normal">· {b.email}</span></p>
            <p className="text-xs text-slate-400 truncate">{b.listings.length ? b.listings.join(", ") : "No listings yet"} · {b.subscription_status === "active" ? "Paying" : "Not paying"}</p>
          </div>
          <Button data-testid={`owner-remove-business-${b.id}`} onClick={() => setTarget(b)} variant="outline"
            className="h-8 rounded-full border-[#FF3B6B]/50 text-[#FF7A7A] bg-transparent hover:bg-[#FF3B6B]/10 text-xs shrink-0">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
          </Button>
        </div>
      ))}
      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="owner-remove-dialog" className="bg-[#151C2C] border-[#26334D] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {target?.name || "this business"}?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              This deletes the account ({target?.email}), takes {target?.listings.length || 0} listing(s) off VacaVibe, and cancels their subscription. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="owner-remove-cancel" className="rounded-full bg-transparent border-[#26334D] text-white hover:bg-[#1E293B] hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="owner-remove-confirm" onClick={remove} className="rounded-full bg-[#FF3B6B] hover:bg-[#E02E5A] text-white">Remove account</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
